import React, { useEffect, useState } from 'react';
import {
  Box,
  Drawer,
  IconButton,
  CircularProgress,
  Typography,
  Alert,
  Button,
} from '@mui/material';
import { Close as CloseIcon, Download as DownloadIcon } from '@mui/icons-material';
import quoteService from '../services/quoteService';

const QuotePreviewDrawer = ({ open, onClose, quoteId }) => {
  const [htmlContent, setHtmlContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && quoteId) {
      loadPreview();
    } else {
      setHtmlContent('');
      setError(null);
    }
  }, [open, quoteId]);

  const loadPreview = async () => {
    if (!quoteId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await quoteService.getPreviewHTML(quoteId);
      if (result.success) {
        setHtmlContent(result.data);
      } else {
        setError(result.error || 'Αποτυχία φόρτωσης preview');
      }
    } catch (err) {
      console.error('Error loading quote preview:', err);
      setError('Αποτυχία φόρτωσης preview');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!quoteId) return;
    
    try {
      const result = await quoteService.downloadPDF(quoteId);
      if (result.success) {
        const link = document.createElement('a');
        link.href = result.data.url;
        link.download = `quote_${quoteId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(result.data.url);
      } else {
        setError(result.error || 'Αποτυχία δημιουργίας PDF');
      }
    } catch (err) {
      console.error('Error downloading PDF:', err);
      setError('Αποτυχία δημιουργίας PDF');
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: '90%', md: '80%' },
          maxWidth: '1200px',
        },
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box
          sx={{
            p: 2,
            borderBottom: '1px solid #e0e0e0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#fff',
            zIndex: 1,
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            Προεπισκόπηση Προσφοράς
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadPDF}
              disabled={loading || !quoteId}
            >
              Κατέβασμα PDF
            </Button>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Content */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            backgroundColor: '#f5f5f5',
            position: 'relative',
          }}
        >
          {loading && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Box sx={{ p: 3 }}>
              <Alert severity="error">{error}</Alert>
            </Box>
          )}

          {!loading && !error && htmlContent && (
            <Box
              sx={{
                maxWidth: '210mm',
                margin: '0 auto',
                backgroundColor: '#fff',
                boxShadow: '0 0 10px rgba(0,0,0,0.1)',
                minHeight: '100%',
              }}
            >
              <iframe
                title="Quote Preview"
                srcDoc={htmlContent}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  minHeight: '800px',
                }}
                onLoad={() => {
                  // Adjust iframe height to content
                  const iframe = document.querySelector('iframe[title="Quote Preview"]');
                  if (iframe && iframe.contentWindow) {
                    try {
                      const iframeDoc = iframe.contentWindow.document;
                      if (iframeDoc.body) {
                        iframe.style.height = `${iframeDoc.body.scrollHeight + 100}px`;
                      }
                    } catch (e) {
                      // Cross-origin or other error, ignore
                    }
                  }
                }}
              />
            </Box>
          )}

          {!loading && !error && !htmlContent && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <Typography color="text.secondary">Δεν υπάρχουν δεδομένα για προεπισκόπηση</Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default QuotePreviewDrawer;
